import React, { useCallback, useMemo, useState } from "react";
import { jsonToCSV } from "react-papaparse";

import {
  ConvertCsvTotDigitalAgronomistCropPracticesRequest,
  saveFile,
  useCSVReader,
  useExportDigitalAgronomistCropPractices,
  useUpdateDigitalAgronomistValues,
} from "utils";

import { COUNTRY_CODE } from "consts";

import { CSV, GetRootProps, StructuredError, UpdateDigitalAgronomistCropPracticesErrorItem, UpdateDigitalAgronomistCropPracticesResponseBody } from "models";

import { AdminHead, AdminTable, Button, Collapse, Flex, Icon, Modal, Statistic, Tooltip, Message as message } from "components";

const ErrorItem = React.memo(({ errorItem }: { errorItem: StructuredError }) => (
  <Flex vertical gap="0.5rem">
    <Flex gap="1rem">
      <strong>Type:</strong>
      <span>{errorItem.type}</span>
    </Flex>
    <Flex gap="1rem">
      <strong>Value:</strong>
      <span>"{errorItem.value}"</span>
    </Flex>
    <Flex gap="1rem">
      <strong>Expected:</strong>
      <span>{errorItem.expected}</span>
    </Flex>
    <Flex gap="1rem">
      <strong>Field:</strong>
      <span>{errorItem.field}</span>
    </Flex>
    <Flex gap="1rem">
      <strong>Message:</strong>
      <span>{errorItem.message}</span>
    </Flex>
  </Flex>
));

export const DigitalAgronomistCropPractices = () => {
  const { data: exportDACropPracticesResponse, isPending } = useExportDigitalAgronomistCropPractices(COUNTRY_CODE.GERMANY);
  const { mutate: updateValues, isPending: isPendingUpdate, data: updateValuesResponse } = useUpdateDigitalAgronomistValues();

  const [saving, setSaving] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const { CSVReader } = useCSVReader();

  const createErrorItems = useMemo(
    () => (cropId: string, errorItems: StructuredError[]) =>
      errorItems.map((errorItem, index) => ({
        key: `${cropId}-${index}`,
        label: cropId,
        children: <ErrorItem errorItem={errorItem} />,
      })),
    [updateValuesResponse] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const CollapsableErrors = React.memo(({ errorList }: { errorList?: UpdateDigitalAgronomistCropPracticesErrorItem[] }) => {
    if (!errorList || errorList.length === 0) {
      return <p>No errors</p>;
    }

    return (
      <>
        <h4>Errors listed by crop id:</h4>
        {errorList.map(({ cropId, errorItems }) => (
          <div key={cropId} style={{ marginBottom: "1rem" }}>
            <Collapse items={createErrorItems(cropId, errorItems)} />
          </div>
        ))}
      </>
    );
  });

  const UploadResultModal = React.memo(
    ({
      visible,
      onClose,
      loading,
      totalCount,
      response,
    }: {
      visible?: boolean;
      onClose: () => void;
      loading: boolean;
      totalCount?: number;
      response?: UpdateDigitalAgronomistCropPracticesResponseBody;
    }) => (
      <Modal
        showModal={visible}
        onCancel={onClose}
        title="Upload Results"
        loading={loading}
        footer={null}
        closeIcon={<Icon iconName="tabler/x" />}
        width={800}
        content={
          <Flex vertical gap="2rem">
            <Flex justify="space-evenly">
              <Statistic title="Total" value={totalCount || 0} />
              <Statistic title="Success" value={response?.processedCount || 0} />
              <Statistic title="Errors" value={response?.errorCount || 0} />
            </Flex>
            {response?.errors && response.errors.length > 0 && <CollapsableErrors errorList={response.errors} />}
          </Flex>
        }
      />
    )
  );

  const tableData = useMemo(() => {
    if (!exportDACropPracticesResponse) return [];
    return exportDACropPracticesResponse.map((crop) => ({
      name: crop.name,
      enabled: crop.enabled,
      ...crop.digitalAgronomistValues,
    }));
  }, [exportDACropPracticesResponse]);

  const dataToSave = useMemo(() => {
    if (!exportDACropPracticesResponse) return null;
    return exportDACropPracticesResponse.map((crop) => ({
      name: crop.name,
      enabled: crop.enabled,
      ...crop.digitalAgronomistValues,
    }));
  }, [exportDACropPracticesResponse]);

  const saveCsv = useCallback(() => {
    if (!dataToSave) {
      message({ content: "Something went wrong while transforming the data.", icon: "error" });
      return;
    }

    setSaving(true);

    // Use setTimeout to prevent UI blocking
    setTimeout(() => {
      try {
        const csv = jsonToCSV(dataToSave);
        saveFile(csv, "DigitalAgronomistCropPractices.csv");
      } catch {
        message({ content: "Error generating CSV file.", icon: "error" });
      } finally {
        setSaving(false);
      }
    }, 0);
  }, [dataToSave]);

  const processFile = (csv: CSV) => {
    setSaving(true);
    setShowUploadModal(true);

    // Use setTimeout to prevent UI blocking
    setTimeout(() => {
      try {
        const convertedCsv = ConvertCsvTotDigitalAgronomistCropPracticesRequest(csv);

        if (convertedCsv.data !== null) {
          updateValues({ data: convertedCsv.data, countryCode: COUNTRY_CODE.GERMANY });
        }

        if (convertedCsv.errors.length > 0) {
          message({ content: "CSV conversion errors occurred", icon: "error" });
        }
      } catch {
        message({ content: "Error processing CSV file", icon: "error" });
      } finally {
        setSaving(false);
      }
    }, 0);
  };

  return (
    <>
      <AdminHead
        headline={`${exportDACropPracticesResponse?.length || 0} Crops`}
        description={
          <>
            IMPORTANT: This will only update Digital Agronomist values for "DE".
            {"\n"}
            {"\n"}
            When updating lists of properties, the CSV file that you upload must contain the same structure as the downloadable CSV (including header and
            quantity of columns). Items will be matched by ID number, which you can leave blank for new additions. Any existing items not included in your CSV
            upload will not be changed.
          </>
        }
        extra={
          <>
            <Tooltip placement="bottom" title="Download this property list">
              <Button size="small" type="secondary" disabled={saving} loading={saving} onClick={saveCsv}>
                <Icon iconName="tabler/download" size="s" />
              </Button>
            </Tooltip>
            <Tooltip placement="bottom" title="Upload property list">
              <CSVReader
                config={{
                  skipEmptyLines: true,
                }}
                noDrag
                style={{
                  dropArea: {
                    border: 0,
                    padding: 0,
                    margin: 0,
                    width: "auto",
                    height: "auto",
                    display: "inline-block",
                  },
                  progressBar: {
                    display: "none !important",
                    opacity: 0,
                  },
                }}
                onUploadAccepted={processFile}
              >
                {({ getRootProps }: { getRootProps: GetRootProps }) => (
                  <Button
                    className="margin-left-50"
                    disabled={saving}
                    dataTestId="module-access-upload-button"
                    loading={saving}
                    size="small"
                    type="secondary"
                    {...getRootProps()}
                  >
                    <Icon iconName="tabler/upload" size="s" />
                  </Button>
                )}
              </CSVReader>
            </Tooltip>
          </>
        }
      />

      {/* @ts-expect-error convert to TS */}
      <AdminTable
        loading={isPending}
        dataSource={tableData}
        rowKey="key"
        config={{
          defaultPageSize: 50, // Reduce page size for better performance
        }}
      />

      <UploadResultModal
        visible={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        loading={isPendingUpdate}
        totalCount={exportDACropPracticesResponse?.length}
        response={updateValuesResponse}
      />
    </>
  );
};
